import { nanoid } from 'nanoid'
import type { Reactive, DeepReadonly } from 'vue'
import { useConfDataQuery } from '~/api/conf-api'
import { buildPath, searchParamsToSortedObject } from '~/utils/urls'
import {
  mapSearchStateToSearchApiParams,
  buildSearchUrl,
} from './search.mappers'
import type { SearchState, SearchBarState } from './search.types'
import {
  FILTER_CONF_MAP,
  isSearchURLParam,
  SEARCH_PAGE_DEFAULT_PATH,
  SEARCH_PAGE_TYPE,
  addDestinationFacet,
  triggerDestinationFacetsRef,
  type FilterConf,
} from './search.conf'
import {
  get,
  isObject,
  set,
  mapValues,
  compact,
  isArray,
  omit,
  merge,
} from 'lodash-es'
import { withQuery } from 'ufo'
import type { RouteLocationNormalized } from 'vue-router'

export function getSearchStateFromRouteConf(
  context: MaybeRefOrGetter<SearchContext>,
  route: MaybeRefOrGetter<RouteLocationNormalized>,
) {
  const { pageType, destination } = toValue(context)
  const { searchParams } = useRouteURL(toValue(route)).value

  if (pageType !== SEARCH_PAGE_TYPE || !destination) return

  return mapDestinationToSearchState(destination, searchParams)
}

export function mapDestinationToSearchState(
  destination: Partial<Destination> = {},
  queryParams?: URLSearchParams,
): SearchState {
  const paramsFromURLQueryParams = Object.fromEntries(
    Object.entries(queryParams ? searchParamsToSortedObject(queryParams) : {})
      .map((entry) => (isSearchURLParam(entry[0]) ? entry : undefined))
      .filter(Boolean) as [string, unknown][],
  )

  return {
    ...mapSearchURLParamsToSearchState({
      ...paramsFromURLQueryParams,
      ...(destination.filters?.reduce(
        (params, { key, value }) => {
          Object.assign(params, { [key]: value === 'true' ? true : value })
          return params
        },
        {} as Record<string, unknown>,
      ) || {}),
    }),
    destination: getDestinationQuery(destination),
  } satisfies SearchState
}

export type EnhancedReactiveSearchState = Reactive<SearchState> & {
  update(newState: SearchState): void
  reset(newState: SearchState): void
  model: Ref<SearchState>
}

export type SearchStateUpdateCallback = (
  target: SearchState,
  prop?: string | symbol,
) => void

export function createReactiveSearchState(
  state: Ref<SearchState>,
  filterChangeCallback?: SearchStateUpdateCallback,
) {
  function onStatePropChange(target: SearchState, prop: string | symbol) {
    // remove page property when non-page property is changed
    if (prop !== 'page' && target.page) {
      target.page = undefined
    }

    filterChangeCallback?.(target, prop)
  }

  function update(reset: boolean, newState: SearchState) {
    // remove page property when filter is changed
    newState = { ...newState, page: undefined }
    state.value = newState

    // does not trigger change callback on reset
    if (reset) return

    filterChangeCallback?.(newState)
  }

  /**
   * To be able to set reference and trigger filter change callback
   * here is convenient model reference for batch update in v-model
   */
  const modelRef = customRef(() => ({
    get() {
      return state.value
    },
    set(newState) {
      update(false, newState)
    },
  }))

  return reactive(
    new Proxy(state.value, {
      set(_, prop, value) {
        Reflect.set(state.value, prop, value)
        onStatePropChange(state.value, prop)
        triggerRef(state)
        return true
      },
      get(_, prop, rec) {
        if (prop === 'update') return update.bind(null, false)
        if (prop === 'reset') return update.bind(null, true)
        if (prop === 'model') return modelRef
        return Reflect.get(state.value, prop, rec)
      },
      deleteProperty(_, prop) {
        const removed = Reflect.deleteProperty(state.value, prop)
        onStatePropChange(state.value, prop)
        triggerRef(state)
        return removed
      },
      has(_, prop) {
        return Reflect.has(state.value, prop)
      },
      ownKeys() {
        return Object.keys(state.value)
      },
      getOwnPropertyDescriptor() {
        return {
          enumerable: true,
          configurable: true,
        }
      },
    }),
  ) as EnhancedReactiveSearchState
}

function useSyncDestinationsFacet(searchState: MaybeRef<SearchState>) {
  const { data: facetData } = useRedesignSearchQuery(
    computed(() => ({
      country: unref(searchState)?.destination?.country,
      region: unref(searchState)?.destination?.region,
      facet: ['countries', 'regions', 'places'],
      pagesize: SEARCH_API_PAGESIZE_NONE,
    })),
  )

  watch(facetData, (data) => {
    if (!data || !data.facets) return
    ;[
      ...(data.facets.countries || []),
      ...(data.facets.regions || []),
      ...(data.facets.places || []),
    ].forEach((destFacet) => {
      addDestinationFacet(destFacet)
    })
    triggerDestinationFacetsRef()
  })
}

export function useSearchState(
  initialState: MaybeRefOrGetter<SearchState>,
  filterChangeCallback?: SearchStateUpdateCallback,
) {
  const state = ref(toValue(initialState))
  useSyncDestinationsFacet(state)

  return createReactiveSearchState(state, filterChangeCallback)
}

export type SearchContext = AppContext & {
  pageType: PageType | undefined
  destination: ConfDestination | undefined
  defaultPath: string | undefined
  filterConfs: SearchFilterConf[] | undefined
  cancelConditions: CancelCondition[] | undefined
}

export function useSearchContext(): Reactive<SearchContext> {
  const appContext = useAppContext()
  const confData = useConfdata()

  return reactive({
    ...toRefs(appContext),
    pageType: computed(() => confData.pagetype),
    destination: computed(() => confData.raw?.destination),
    defaultPath: computed(() => confData.prefixSearchpage),
    filterConfs: computed(
      () =>
        (confData.additionalBody as SearchPageBody | undefined)
          ?.searchfilterconfs,
    ),
    cancelConditions: computed(() => confData.cancelConditions),
  })
}

export function generateSearchUrl(
  context: MaybeRefOrGetter<SearchContext>,
  searchState: MaybeRefOrGetter<SearchState>,
  destinationSlug?: MaybeRefOrGetter<string>,
) {
  const { languagePrefix, defaultPath, filterConfs } = toValue(context)
  const searchStateValue = toValue(searchState)

  return buildSearchUrl(mapSearchStateToURLParams(searchStateValue), {
    destinationSlug: toValue(destinationSlug),
    prefix: languagePrefix,
    defaultPath: defaultPath || SEARCH_PAGE_DEFAULT_PATH,
    filtersConf: filterConfs ?? [],
  })
}

export function useSearchSeason(
  query: MaybeRefOrGetter<{ checkin?: UnparsedDate }>,
) {
  const globalParams = useParams()

  return computed(
    () =>
      parseSeason(globalParams.all.season) ?? getSeason(toValue(query).checkin),
  )
}

export function useSearchResults(searchState: Reactive<SearchState>) {
  const query = computed(() => mapSearchStateToSearchApiParams(searchState))

  const {
    data,
    isLoading: isSearchLoading,
    isPending,
    isFetching,
  } = useRedesignSearchQuery(query)

  const key = computed(
    () => `${nanoid()}_${JSON.stringify(data.value?._meta.params)}`,
  )
  const items = computed(() => data.value?.docs || [])
  const viewport = computed(() => data.value?.viewport)
  const facets = computed(() => data.value?.facets)
  const currentPage = toRef(searchState, 'page', 1)
  const totalPages = computed(() => data.value?.totalPages ?? 0)
  const total = computed(() => data.value?.totalHits ?? 0)
  const isLoading = computed(() => isSearchLoading.value || isPending.value)

  return reactive({
    /**
     * Unique result identifier that depends on the query params
     * and is updated each time a new response is received.
     */
    key,
    isLoading,
    isFetching,
    items,
    facets,
    viewport,
    currentPage,
    total,
    totalPages,
  })
}

export interface FilterSelected {
  key: string
  value: unknown
  type: FilterType
}

export function useFilters(searchState: Ref<SearchState>) {
  const all = mapValues(FILTER_CONF_MAP, (conf) => useFilter(conf, searchState))

  const selected = computed(() => {
    return Object.values(all).flatMap((filter) => filter.selected)
  })

  function remove(key: string, value: unknown) {
    all[key]?.remove(value)
  }

  /**
   * Remove several filters from the search state
   * @param keys - Filter keys to be removed. If not provided, all selected filters will be removed.
   */
  function removeBatch(keys?: string[]) {
    searchState.value = merge(
      mapSearchURLParamsToSearchState({}),
      omit(searchState.value, keys ?? selected.value.map(({ key }) => key)),
    )
  }

  return shallowReadonly({
    all: reactive(all),
    selected: reactive(selected),
    remove,
    removeBatch,
  })
}

const FILTER_KEYS_PERMANENT = ['destination', 'dates', 'pax', 'pets', 'minpets']
const FILTER_KEYS_SYSTEM = ['page', 'sorting']

export function useFilter(conf: FilterConf, searchState: Ref<SearchState>) {
  const tracking = useTracking()
  const generateUniqueKey = (value: unknown) =>
    isObject(value) ? Object.values(value).join('-') : value

  const baseEnabled = computed(() => conf.enabled())

  const { data, isPending, isLoading } = useRedesignSearchQuery(
    () => ({
      ...mapSearchStateToSearchApiParams(searchState.value),
      pagesize: SEARCH_API_PAGESIZE_NONE,
    }),
    {
      enabled: baseEnabled,
    },
  )

  const loading = computed(() => isLoading.value || isPending.value)

  const filterOptions = computed(() =>
    data.value?.facets ? getFilterOptions(conf, data.value.facets) : [],
  )

  const counts = computed(() => {
    const countsMap = new Map()

    filterOptions.value.forEach(({ value, count }) =>
      countsMap.set(generateUniqueKey(value), count),
    )

    return countsMap
  })

  const options = computed<FilterOption[]>(() => {
    const countsMap = counts.value

    return filterOptions.value.map((opt) => {
      return {
        ...opt,
        count: countsMap.get(generateUniqueKey(opt.value)) ?? 0,
      }
    })
  })

  const enabled = computed(() => {
    if (!baseEnabled.value) return false

    if (loading.value) return true

    if (!data.value?.facets) return false

    /**
     * IHGWEBCC-1789 Hide distance filter if it doesn't exist for a destination
     */
    if (
      DISTANCE_FILTERS.includes(conf.key as (typeof DISTANCE_FILTERS)[number])
    ) {
      return filterOptions.value.some((option) => (option.count ?? 0) > 0)
    }

    return true
  })

  const value = customRef(() => ({
    get() {
      return get(searchState.value, conf.key)
    },
    set(newValue) {
      if (import.meta.client) {
        const oldValue = get(searchState.value, conf.key)

        tracking.handlers?.redesignFilters.filter(conf, oldValue, newValue)
      }

      set(searchState.value, conf.key, newValue)
      searchState.value = { ...searchState.value }
    },
  }))

  const selected = computed<FilterSelected[]>(() => {
    const { key, type } = conf

    if (FILTER_KEYS_PERMANENT.includes(key)) return []
    if (FILTER_KEYS_SYSTEM.includes(key)) return []
    if (!value.value) return []
    if (isNumInterval(value.value) && isNumIntervalEmpty(value.value)) return []

    return isArray(value.value) && !isNumInterval(value.value)
      ? compact(value.value).flatMap((val) => ({ key, type, value: val }))
      : [{ key, type, value: value.value }]
  })

  function remove(valueToRemove: unknown) {
    switch (conf.type) {
      case 'single':
        value.value = undefined
        break
      case 'multi':
        value.value =
          value.value?.filter((entry: unknown) => entry !== valueToRemove) ?? []
        break
      case 'range':
        value.value = [0, 0]
        break
      case 'stepper':
        value.value = 0
        break
      default:
        break
    }
  }

  return reactive({
    enabled,
    value,
    options,
    selected,
    loading,
    remove,
  })
}

export function useSearchDestinationBreadcrumbs(
  route: MaybeRefOrGetter<RouteLocationNormalized> = useRoute(),
) {
  const { data } = useConfDataQuery(useConfDataURL(useRouteURL(route)))

  return computed(() => {
    if (!data.value?.destination) return []

    const { languageprefix: prefix, destination } = data.value
    const { country, region, place } = splitDestinationPath(destination.urlPath)

    return [
      { name: destination.country, slugs: [country] },
      { name: destination.region, slugs: [country, region] },
      { name: destination.place, slugs: [country, region, place] },
    ]
      .filter(({ name }) => !!name)
      .map(({ name, slugs }) => ({
        name: name!,
        path: buildPath(prefix, ...slugs),
      }))
  })
}

export function useSearchBarRoute(
  state: MaybeRefOrGetter<DeepReadonly<SearchBarState>>,
  extraParams?: MaybeRefOrGetter<Record<string, unknown> | undefined>,
) {
  const context = useSearchContext()

  const fullPath = computed(() => {
    const stateValue = toValue(state)
    const searchState = mapSearchBarStateToSearchState(stateValue)
    const { destination } = stateValue

    if (destination && isSuggestionTypeAccom(destination.type))
      return getAccomUrl(
        context,
        destination,
        getTripParamsFromState(searchState),
      )

    return withQuery(
      getUrlFullPath(
        generateSearchUrl(context, searchState, destination?.slug),
      ),
      unpack(toValue(extraParams) ?? {}),
    )
  })

  return reactive({ fullPath })
}

export function useSearchSuggestions(query: MaybeRefOrGetter<string>) {
  const result = useSearchApiSuggestionsQuery(query)

  /**
   * Sync suggestions data with destination facets map
   */
  watch(result.data, (data) =>
    data?.forEach((suggestion) => {
      mapSuggestionToDestinationFacets(suggestion).forEach(addDestinationFacet)
    }),
  )

  return result
}

export function useRedesignSearchSuggestions(query: MaybeRefOrGetter<string>) {
  const result = useRedesignSearchApiSuggestionsQuery(query)

  /**
   * Sync suggestions data with destination facets map
   */
  watch(result.data, (data) =>
    data?.forEach((suggestion) => {
      mapRedesignSuggestionToDestinationFacets(suggestion).forEach(
        addDestinationFacet,
      )
    }),
  )

  return result
}

export function useSearchBarToSearchStateApply() {
  const context = useSearchContext()
  const fetchConfData = useLazyConfDataFetch({ followRedirect: true })

  return async function mergeSearchBarState(
    searchBarState: MaybeRefOrGetter<
      SearchBarState | DeepReadonly<SearchBarState>
    >,
    searchState: MaybeRefOrGetter<SearchState>,
  ): Promise<SearchState> {
    const barStateValue = toValue(searchBarState)
    const searchStateValue = toValue(searchState)

    const { slug } = barStateValue.destination ?? {}

    const stateWithoutDestination: SearchState = {
      ...omit(searchStateValue, 'destination'),
      ...mapSearchBarStateToSearchState(barStateValue),
    }

    const destinationConf = slug
      ? (
          await fetchConfData(
            getUrlFullPath(
              generateSearchUrl(context, stateWithoutDestination, slug),
            ),
          )
        )?.destination
      : undefined

    const suggestion = mapDestinationToSuggestion(destinationConf)

    if (suggestion) {
      const destinationFacets = mapSuggestionToDestinationFacets(suggestion)

      destinationFacets.map(addDestinationFacet)
    }

    return merge(stateWithoutDestination, {
      destination: getDestinationQuery(destinationConf ?? {}),
    })
  }
}
